import InfoIcon from "@mui/icons-material/Info";
import {
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Step from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { getUserInfo } from "../../../services/auth.service";
import {
  createSubscription,
  getUserOnly,
} from "../../../services/expositor/subscriptions.service";
import { ModalComponent, useModal } from "../../../ui-components/Modal";
import { useToast } from "../../../ui-components/ToastProvider";
import { parseDate, parseDateHifen } from "../../../utils/parseDate";

function getSubscriptionPriceDinamic(
  spaceType,
  dayCount = 1,
  extraChairs = 0,
  extraTables = 0,
  extraBike = false,
  extraTrailer = false,
  eventData,
) {
  const d = Math.max(1, parseInt(dayCount, 10) || 1);
  const qChairs = Math.max(0, Number(extraChairs) || 0);
  const qTables = Math.max(0, Number(extraTables) || 0);

  const pricing = eventData?.[spaceType] || {};

  const sumForDays = (p1, p2) => {
    const one = Number(p1) || 0;
    const two = Number(p2) || 0;
    if (d === 1) return one;
    if (d === 2) return two > 0 ? two : one * 2;
    const baseTwoDays = two > 0 ? two : one * 2;
    return baseTwoDays + (d - 2) * one;
  };

  const base = sumForDays(
    pricing.subscription_price_1,
    pricing.subscription_price_2,
  );
  const chair =
    qChairs * sumForDays(pricing.chair_price_1, pricing.chair_price_2);
  const table =
    qTables * sumForDays(pricing.table_price_1, pricing.table_price_2);
  const bike = extraBike ? (Number(pricing.bike) || 0) : 0;
  const trailer = extraTrailer ? (Number(pricing.trailer) || 0) : 0;

  return Math.round((base + chair + table + bike + trailer) * 100) / 100;
}

function parseCurrency(x) {
  if (typeof x !== "string") {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  }
  const cleaned = x
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

const PriceTable = ({ eventData }) => {
  let data = eventData;
  try {
    if (typeof data === "string") data = JSON.parse(data);
  } catch (e) {}

  const oneTent = data?.["autoral"] || {
    subscription_price_1: 0,
    subscription_price_2: 0,
    table_price_1: 0,
    table_price_2: 0,
    chair_price_1: 0,
    chair_price_2: 0,
    percentage: 0,
  };

  const twoTents = data?.["alimentacao"] || {
    subscription_price_1: 0,
    subscription_price_2: 0,
    table_price_1: 0,
    table_price_2: 0,
    chair_price_1: 0,
    chair_price_2: 0,
    percentage: 0,
    bike: 0,
    trailer: 0,
  };

  const formatBRL = (value) => {
    const num = Number(value);
    if (!isFinite(num)) return "—";
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  return (
    <div className="flex flex-col gap-3">
      {/* 1 TENDA SECTION */}
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#24324D" }}>
          Espaço Exclusivo (2mx2m) MARCAS AUTORAIS
        </Typography>
        <Typography sx={{ color: "#64748b", mb: 2 }}>
          Inclui tenda, 1 cadeira e acesso à energia 220V
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Chip label="1 dia" size="small" />
                <Typography sx={{ fontWeight: 700 }}>
                  {formatBRL(oneTent.subscription_price_1)}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Mesa</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(oneTent.table_price_1)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Cadeira</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(oneTent.chair_price_1)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>% Vendas</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {oneTent.percentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Chip label="2 dias" size="small" />
                <Typography sx={{ fontWeight: 700 }}>
                  {formatBRL(oneTent.subscription_price_2)}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Mesa</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(oneTent.table_price_2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Cadeira</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(oneTent.chair_price_2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>% Vendas</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {oneTent.percentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 2 TENDAS SECTION */}
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#24324D" }}>
          Espaço Exclusivo (2mx2m) PRAÇA DE ALIMENTAÇÃO
        </Typography>
        <Typography sx={{ color: "#64748b", mb: 2 }}>
          Inclui tenda, 1 cadeira e acesso à energia 220V
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Chip label="2 dias" size="small" />
                <Typography sx={{ fontWeight: 700 }}>
                  {formatBRL(twoTents.subscription_price_2)}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Mesa</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(twoTents.table_price_2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Cadeira</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(twoTents.chair_price_2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Bike</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(twoTents.bike)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>Trailer</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatBRL(twoTents.trailer)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#475569" }}>% Vendas</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {twoTents.percentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

//TOdo get the event and create subscription logic
export default function CreateSubscriptionForm({
  date,
  events,
  handleClose,
  event,
}) {
  const { showToast } = useToast();
  const [activeStep, setActiveStep] = React.useState(0);
  const [formData, setFormData] = React.useState({});
  const [userSubs, setUserSubs] = React.useState([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      setUserSubs(await getUserOnly());
    })();
  }, []);

  const chosenevent = event;

  React.useEffect(() => {
    setFormData((state) => ({
      ...state,
      user_id: getUserInfo().id,
      event_id: chosenevent.id,
      start_date: parseDateHifen(date),
      end_date: parseDateHifen(date),
      extra_table: userSubs.filter((x) => x.extra_table)[0]?.extra_table || 0,
      extra_chair: userSubs.filter((x) => x.extra_chair)[0]?.extra_chair || 0,
      extra_bike: userSubs.filter((x) => x.extra_bike)[0]?.extra_bike || 0,
      extra_trailer: userSubs.filter((x) => x.extra_trailer)[0]?.extra_trailer || 0,
      space_type: "autoral",
      table_quantity:
        userSubs.filter((x) => x.table_quantity)[0]?.table_quantity || 0,
      chair_quantity:
        userSubs.filter((x) => x.chair_quantity)[0]?.chair_quantity || 0,
      notes: userSubs.filter((x) => x.notes)[0]?.notes || "",
      items:
        getUserInfo()?.brand_items ||
        userSubs.filter((x) => x.items)[0]?.items ||
        "",
    }));
  }, [userSubs, date, chosenevent.id]);

  const steps = [
    {
      label: "Informaçoes sobre a pré-inscrição e tabela de preços",
      description: (
        <>
          <PriceTable eventData={event.event_data}></PriceTable>
        </>
      ),
    },
    {
      label: "Selecionar dia/dias",
      description: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="start_date"
            name="start_date"
            label="Data de inicio da participacao"
            type={"date"}
            onChange={(e) => {
              const newStart = e.target.value;
              setFormData((state) => {
                const newState = {
                  ...state,
                  start_date: newStart,
                };
                if (state.end_date && newStart > state.end_date) {
                  newState.end_date = newStart;
                }
                return newState;
              });
            }}
            value={formData.start_date || parseDateHifen(date)}
            autoComplete="start_date"
            variant="outlined"
            fullWidth
            InputProps={{
              inputProps: {
                min: parseDateHifen(chosenevent.starts_at),
                max: parseDateHifen(chosenevent.ends_at),
              },
            }}
            required
          />
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="end_date"
            name="end_date"
            label="Data de Fim da participacao"
            type={"date"}
            value={formData.end_date || parseDateHifen(date)}
            onChange={(e) => {
              setFormData((state) => ({ ...state, end_date: e.target.value }));
            }}
            autoComplete="start_date"
            variant="outlined"
            fullWidth
            InputProps={{
              inputProps: {
                min:
                  formData.start_date || parseDateHifen(chosenevent.starts_at),
                max: parseDateHifen(chosenevent.ends_at),
              },
            }}
            required
          />
        </div>
      ),
    },
    {
      label: "Escolher tipo de espaço",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="space_type"
            id="space_type"
            name="space_type"
            defaultValue={"autoral"}
            autoFocus
            onFocus={(e) => {
              const data = e.target.nextSibling.value;
              setFormData((state) => ({
                ...state,
                space_type: data,
              }));
            }}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((state) => {
                const newState = {
                  ...state,
                  space_type: val,
                };
                if (val === "alimentacao") {
                  newState.start_date = parseDateHifen(chosenevent.starts_at);
                  newState.end_date = parseDateHifen(chosenevent.ends_at);
                }
                return newState;
              });
            }}
            fullWidth
          >
            <MenuItem key={2} value={"autoral"}>
              Espaço Exclusivo (2mx2m) MARCAS AUTORAIS
            </MenuItem>
            <MenuItem key={2} value={"alimentacao"}>
              Espaço Exclusivo (2mx2m) PRAÇA DE ALIMENTAÇÃO
            </MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja cadeira extra?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="chair_quantity"
            id="chair_quantity"
            name="chair_quantity"
            value={formData.chair_quantity || 0}
            onChange={(e) => {
              const qty = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_chair: qty > 0,
                chair_quantity: qty,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo cadeira extra</MenuItem>
            <MenuItem value={1}>Desejo uma cadeira</MenuItem>
            <MenuItem value={2}>Desejo duas cadeiras</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja mesa?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="table_quantity"
            id="table_quantity"
            name="table_quantity"
            value={formData.table_quantity || 0}
            onChange={(e) => {
              const qty = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_table: qty > 0,
                table_quantity: qty,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo mesa extra</MenuItem>
            <MenuItem value={1}>Desejo uma mesa</MenuItem>
            <MenuItem value={2}>Desejo duas mesas</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja Bike?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="extra_bike"
            id="extra_bike"
            name="extra_bike"
            value={formData.extra_bike || 0}
            onChange={(e) => {
              const val = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_bike: val,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo bike</MenuItem>
            <MenuItem value={1}>Desejo bike</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja Trailer?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="extra_trailer"
            id="extra_trailer"
            name="extra_trailer"
            value={formData.extra_trailer || 0}
            onChange={(e) => {
              const val = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_trailer: val,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo trailer</MenuItem>
            <MenuItem value={1}>Desejo trailer</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label:
        "Descreva TODOS os itens que você pretende levar para vender na Praceria (lembrando que só são permitidos itens autorais - não é autorizado revenda). Vale dizer que os itens poderão ser aprovados parcialmente ou integralmente pela Curadoria.",
      description: (
        <>
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="items"
            name="items"
            label="Itens"
            placeholder="Liste um a um os itens que
            você produz e pretende levar
            na Praceria."
            onChange={(e) => {
              setFormData((state) => ({ ...state, items: e.target.value }));
            }}
            autoFocus
            onFocus={(e) => {
              setFormData((state) => ({
                ...state,
                items: e.target.value,
              }));
            }}
            autoComplete="items"
            variant="outlined"
            defaultValue={
              getUserInfo()?.brand_items ||
              userSubs.filter((x) => x.items)[0]?.items
            }
            rows={4}
            multiline
            fullWidth
            required
          />
        </>
      ),
    },
    {
      label: "Alguma observação?",
      description: (
        <>
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="notes"
            name="notes"
            label="Notas"
            onChange={(e) => {
              setFormData((state) => ({ ...state, notes: e.target.value }));
            }}
            autoFocus
            onFocus={(e) => {
              setFormData((state) => ({
                ...state,
                notes: e.target.value,
              }));
            }}
            autoComplete="notes"
            variant="outlined"
            defaultValue={userSubs.filter((x) => x.notes)[0]?.notes}
            rows={4}
            multiline
            fullWidth
            required
          />
        </>
      ),
    },
  ];

  const handleNext = (e) => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (formData.space_type && formData.start_date && formData.end_date) {
      const toNum = parseCurrency;
      let parsed = {};
      try {
        parsed =
          typeof event.event_data === "string"
            ? JSON.parse(event.event_data || "{}")
            : event.event_data || {};
      } catch {
        parsed = {};
      }

      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const days = Math.max(
        1,
        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
      );
      const chairsQty = formData.extra_chair
        ? toNum(formData.chair_quantity || 0)
        : 0;
      const tablesQty = formData.extra_table
        ? toNum(formData.table_quantity || 0)
        : 0;
      const amount = getSubscriptionPriceDinamic(
        formData.space_type,
        days,
        chairsQty,
        tablesQty,
        formData.extra_bike,
        formData.extra_trailer,
        parsed,
      );
      setFormData((state) => ({ ...state, amount: amount }));
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    const toNum = parseCurrency;
    let payload = { ...formData };
    if (
      !payload.amount &&
      payload.space_type &&
      payload.start_date &&
      payload.end_date
    ) {
      let parsed = {};
      try {
        parsed =
          typeof event.event_data === "string"
            ? JSON.parse(event.event_data || "{}")
            : event.event_data || {};
      } catch {
        parsed = {};
      }

      const start = new Date(payload.start_date);
      const end = new Date(payload.end_date);
      const days = Math.max(
        1,
        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
      );
      const chairsQty = payload.extra_chair
        ? toNum(payload.chair_quantity || 0)
        : 0;
      const tablesQty = payload.extra_table
        ? toNum(payload.table_quantity || 0)
        : 0;
      payload.amount = getSubscriptionPriceDinamic(
        payload.space_type,
        days,
        chairsQty,
        tablesQty,
        payload.extra_bike,
        payload.extra_trailer,
        parsed,
      );
    }
    await createSubscription(payload);
  };

  const { open, handleOpen, handleClose: hClose } = useModal();

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!isFinite(num)) return null;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  React.useEffect(() => {
    const toNum = parseCurrency;
    if (formData.space_type && formData.start_date && formData.end_date) {
      let parsed = {};
      try {
        parsed =
          typeof event.event_data === "string"
            ? JSON.parse(event.event_data || "{}")
            : event.event_data || {};
      } catch {
        parsed = {};
      }

      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const days = Math.max(
        1,
        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
      );
      const chairsQty = formData.extra_chair
        ? toNum(formData.chair_quantity || 0)
        : 0;
      const tablesQty = formData.extra_table
        ? toNum(formData.table_quantity || 0)
        : 0;
      const nextAmount = getSubscriptionPriceDinamic(
        formData.space_type,
        days,
        chairsQty,
        tablesQty,
        formData.extra_bike,
        formData.extra_trailer,
        parsed,
      );
      setFormData((s) =>
        s.amount === nextAmount ? s : { ...s, amount: nextAmount },
      );
    }
  }, [
    formData.space_type,
    formData.start_date,
    formData.end_date,
    formData.extra_chair,
    formData.extra_table,
    formData.chair_quantity,
    formData.table_quantity,
    formData.extra_bike,
    formData.extra_trailer,
    event.event_data,
  ]);

  const getDaysCount = () => {
    if (!formData.start_date || !formData.end_date) return null;
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const diff = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 1;
  };

  return (
    <div className="w-full">
      {activeStep !== 0 && activeStep !== steps.length && (
        <>
          <Button
            variant="contained"
            onClick={handleOpen}
            startIcon={<InfoIcon />}
            sx={{
              backgroundColor: "#24324D",
              color: "#ffffff",
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(36,50,77,0.25)",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: "#1a2538",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 14px rgba(36,50,77,0.35)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Ver tabela de preços novamente
          </Button>
          <ModalComponent open={open} handleClose={hClose}>
            <PriceTable eventData={event.event_data}></PriceTable>
            <p style={{ margin: "10px" }}>
              Clique em qualquer lugar para fechar a imagem
            </p>
          </ModalComponent>
        </>
      )}
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        sx={{
          mb: 2,
          "& .MuiStepIcon-root": { color: "#cbd5e1" },
          "& .MuiStepIcon-root.Mui-active": { color: "#24324D" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#24324D" },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              {step.description}
              <Box sx={{ mb: 2 }}>
                <div>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      mt: 1,
                      mr: 1,
                      backgroundColor: "#24324D",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#1a2538",
                        boxShadow: "0 4px 12px rgba(36,50,77,0.3)",
                      },
                    }}
                  >
                    {index === steps.length - 1 ? "Finalizar" : "Continuar"}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1, color: "#24324D" }}
                  >
                    Voltar
                  </Button>
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          <Typography my={2} sx={{ fontWeight: 600, color: "#24324D" }}>
            Revise sua pré-inscrição
          </Typography>

          <Grid sx={{ display: "grid", gap: 1.5 }}>
            {/* <Typography>
              <span style={{ color: "#a8518e" }}>Evento: </span>
              {formData.name || "—"}
            </Typography> */}

            <Typography>
              <span style={{ fontWeight: 600 }}>Período: </span>
              {parseDate(formData.start_date)} a {parseDate(formData.end_date)}
              {getDaysCount()
                ? ` (${getDaysCount()} dia${getDaysCount() > 1 ? "s" : ""})`
                : ""}
            </Typography>

            <Typography>
              <span style={{ fontWeight: 600 }}>Tipo de espaço: </span>
              {formData.space_type === "autoral"
                ? "Espaço Exclusivo (2mx2m) MARCAS AUTORAIS"
                : formData.space_type === "alimentacao"
                  ? "Espaço Exclusivo (2mx2m) PRAÇA DE ALIMENTAÇÃO"
                  : formData.space_type}
            </Typography>

            <Typography>
              <span style={{ fontWeight: 600 }}>Mesa: </span>
              {formData.extra_table
                ? `Sim (${formData.table_quantity || 1})`
                : "Não"}
            </Typography>

            <Typography>
              <span style={{ fontWeight: 600 }}>Cadeira extra: </span>
              {formData.extra_chair
                ? `Sim (${formData.chair_quantity || 1})`
                : "Não"}
            </Typography>

            <Typography>
              <span style={{ fontWeight: 600 }}>Bike: </span>
              {formData.extra_bike ? "Sim" : "Não"}
            </Typography>

            <Typography>
              <span style={{ fontWeight: 600 }}>Trailer: </span>
              {formData.extra_trailer ? "Sim" : "Não"}
            </Typography>

            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              <span style={{ fontWeight: 600 }}>Itens: </span>
              {formData.items || "—"}
            </Typography>

            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              <span style={{ fontWeight: 600 }}>Observações: </span>
              {formData.notes || "—"}
            </Typography>

            <Typography>
              <span style={{ fontWeight: 600 }}>Valor estimado: </span>
              {formatCurrency(formData.amount) || "conforme tabela do evento"}
            </Typography>
          </Grid>

          <Typography sx={{ mt: 2, color: "#64748b", fontSize: "0.9rem" }}>
            Revise os dados antes de enviar. A curadoria pode aprovar parcial ou
            integralmente os itens informados.
          </Typography>

          <Grid flex sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              disabled={isSubmitting}
              onClick={() => {
                handleClose();
              }}
              sx={{
                mt: 1,
                mr: 1,
                borderColor: "#e2e8f0",
                color: "#24324D",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={async () => {
                if (isSubmitting) return;
                setIsSubmitting(true);
                try {
                  await handleSubmit();
                  showToast("Pré-inscrição criada com sucesso!", {
                    severity: "success",
                  });
                  handleClose();

                  setTimeout(() => {
                    window.location.reload();
                  }, 4000);
                } catch (err) {
                  showToast(
                    "Falha ao criar a pré-inscrição. Tente novamente.",
                    {
                      severity: "error",
                    },
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }}
              sx={{
                mt: 1,
                mr: 1,
                backgroundColor: "#24324D",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#1a2538",
                  boxShadow: "0 4px 12px rgba(36,50,77,0.3)",
                },
              }}
            >
              {isSubmitting ? "Enviando..." : "Quero me pré-inscrever"}
            </Button>
          </Grid>
        </Paper>
      )}
    </div>
  );
}
